import { useEffect, useState } from 'react'

import { EMPTY_STRING } from '../../../../constants/common'
import { PdfViewerEvent, PdfViewerFindType } from '../../PdfViewer.constants'

import {
  checkIsNewPdfViewerFind,
  rewindPdfViewerMatchCursor,
} from './usePdfViewerSearch.utils'
import { usePdfViewerSearchResults } from './usePdfViewerSearchResults'

export function usePdfViewerSearch({
  eventBusRef,
  findControllerRef,
  pdfDocument,
  search,
}) {
  const [query, setQuery] = useState(search ?? EMPTY_STRING)
  const [isCaseSensitive, setCaseSensitive] = useState(false)
  const [isEntireWord, setEntireWord] = useState(false)
  const [lastSearch, setLastSearch] = useState(search)

  const { invalidateResults, resetResults, resultsProps, startLoading } =
    usePdfViewerSearchResults({ eventBusRef, findControllerRef, pdfDocument })

  // a consumer-driven query has to land in the panel input as well
  if (search !== lastSearch) {
    setLastSearch(search)
    setQuery(search ?? EMPTY_STRING)
  }

  function dispatchFind({
    caseSensitive = isCaseSensitive,
    entireWord = isEntireWord,
    findPrevious,
    nextQuery,
    type,
  }) {
    invalidateResults()

    eventBusRef.current?.dispatch(PdfViewerEvent.Find, {
      caseSensitive,
      entireWord,
      findPrevious,
      highlightAll: true,
      query: nextQuery,
      type,
    })
  }

  // pdf.js forgets the query with the old document; `null` is an explicit clear,
  // while a consumer that never passes `search` must not search on load
  useEffect(() => {
    if (pdfDocument && (search !== undefined || query)) {
      dispatchFind({ nextQuery: query, type: PdfViewerFindType.New })
    }
  }, [pdfDocument, search])

  function findMatches({ nextQuery, type, ...findOptions }) {
    if (nextQuery && checkIsNewPdfViewerFind(type)) {
      startLoading()
    }

    dispatchFind({ ...findOptions, nextQuery, type })
  }

  function handleChangeQuery(event) {
    const nextQuery = event.target.value

    setQuery(nextQuery)

    if (!nextQuery) {
      resetResults()
    }

    findMatches({ nextQuery, type: PdfViewerFindType.New })
  }

  function handleSubmitQuery() {
    if (query) {
      findMatches({ nextQuery: query, type: PdfViewerFindType.Again })
    }
  }

  function handleClearQuery() {
    setQuery(EMPTY_STRING)
    resetResults()

    findMatches({ nextQuery: EMPTY_STRING, type: PdfViewerFindType.New })
  }

  function handleFindNext() {
    findMatches({ nextQuery: query, type: PdfViewerFindType.Again })
  }

  function handleFindPrevious() {
    findMatches({
      findPrevious: true,
      nextQuery: query,
      type: PdfViewerFindType.Again,
    })
  }

  function handleSelectResult({ matchIndex, pageIndex }) {
    rewindPdfViewerMatchCursor({
      findController: findControllerRef.current,
      matchIndex,
      pageIndex,
    })

    findMatches({ nextQuery: query, type: PdfViewerFindType.Again })
  }

  function handleToggleCaseSensitive() {
    const nextValue = !isCaseSensitive

    setCaseSensitive(nextValue)

    if (query) {
      findMatches({
        caseSensitive: nextValue,
        nextQuery: query,
        type: PdfViewerFindType.New,
      })
    }
  }

  function handleToggleEntireWord() {
    const nextValue = !isEntireWord

    setEntireWord(nextValue)

    if (query) {
      findMatches({
        entireWord: nextValue,
        nextQuery: query,
        type: PdfViewerFindType.New,
      })
    }
  }

  return {
    ...resultsProps,
    isCaseSensitive,
    isEntireWord,
    query,
    onChangeQuery: handleChangeQuery,
    onClearQuery: handleClearQuery,
    onFindNext: handleFindNext,
    onFindPrevious: handleFindPrevious,
    onSelectResult: handleSelectResult,
    onSubmitQuery: handleSubmitQuery,
    onToggleCaseSensitive: handleToggleCaseSensitive,
    onToggleEntireWord: handleToggleEntireWord,
  }
}
